'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';

export default function CircularCarousel({
  children,
  interval = 4000,
}: {
  children: React.ReactNode[];
  interval?: number;
}) {
  const [active, setActive] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const count = children.length;

  // Auto-scroll logic
  useEffect(() => {
    if (count <= 1) return;

    const timer = setInterval(() => {
      if (!scrollRef.current) return;

      const nextIndex = (active + 1) % count;
      const scrollAmount = scrollRef.current.clientWidth * nextIndex;

      scrollRef.current.scrollTo({
        left: scrollAmount,
        behavior: 'smooth',
      });
    }, interval);

    return () => clearInterval(timer);
  }, [count, active, interval]);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollLeft, clientWidth } = scrollRef.current;
    if (clientWidth === 0) return;

    const newActive = Math.round(scrollLeft / clientWidth);
    if (newActive !== active && newActive >= 0 && newActive < count) {
      setActive(newActive);
    }
  }, [active, count]);

  return (
    <div className="relative w-full flex flex-col gap-6">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex w-full overflow-x-auto snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        {children.map((child, i) => (
          <div key={`carousel-item-${i}`} className="w-full flex-shrink-0 snap-center snap-always">
            {child}
          </div>
        ))}
      </div>

      {count > 1 && (
        <div className="flex justify-center gap-2 pb-2">
          {Array.from({ length: count }).map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => {
                if (scrollRef.current) {
                  scrollRef.current.scrollTo({
                    left: i * scrollRef.current.clientWidth,
                    behavior: 'smooth',
                  });
                }
              }}
              className="group relative p-2 focus:outline-none"
            >
              <div
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  active === i ? 'bg-[#166534] w-7' : 'bg-[#166534]/30 w-3.5'
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
